import React, { Dispatch, SetStateAction, useState } from "react"
import {
  CartContainer,
  CartItems,
  Label
} from "../../Order/ManageOrder/DeliverOrder/Cart/Cart.styled"
import * as graphql from "lib/types/generated/graphql-types"
import {
  CustomReasonCode,
  Maybe,
  Order,
  OrderHistory,
  OrderOperationSearchHit,
  OrderOperationSearchHits,
  OrderModificationOrderLines,
  OrderOrderLinesModificationSearchHit
} from "lib/types/generated/graphql-types"
import Reason from "../../Order/Updates/Reason"
import { ArrowIcon, OrderLabel } from "../Shared.styled"
import { Link } from "react-router-dom"
import Money from "../../Money"
import DineroFactory from "dinero.js"
import StartOperation from "./StartOperation"
import { ApolloQueryResult, useMutation } from "@apollo/client"
import START_ORDER_LINES_MODIFICATION from "../../../graphql/mutations/order/modification/orderLines/StartModification"
import RESTART_ORDER_LINES_MODIFICATION from "../../../graphql/mutations/order/modification/orderLines/RestartModification"
import { handleErrorMessages } from "helpers/errors"
import ErrorMessages from "../../Order/ManageOrder/ErrorMessages"
import SecondaryButton from "../../Ui/Buttons/SecondaryButton"
import CopyButton from "../../Ui/CopyButton"
import {
  Container,
  Title,
  ProviderWrapper,
  Provider,
  Providers,
  ActionWrapper,
  LabelWrapper,
  ButtonWrapper
} from "../../Order/ManageOrder/DeliverOrder/FailedDeliveries.styled"
import ActionInfoBox from "./ActionInfoBox"
import { ActionDropdownButton } from "../../Ui/Buttons/ActionDropdownButton"
import { ReactComponent as PaymentIcon } from "../../../images/icons/credit-card.svg"
import {
  TotalsComparison,
  TotalsHeader,
  TotalsRow,
  TotalsLabel,
  TotalsCell,
  OrderLinesTable,
  TableHeader,
  TableBody,
  TypeBadge,
} from "./OrderLinesModificationAction.styled"

type Props = {
  operation: OrderOperationSearchHit
  order: Order
  orderHistory: OrderHistory
  getCustomCode: (code: Maybe<string> | undefined) => CustomReasonCode
  setShow: Dispatch<SetStateAction<boolean>>
  refetchOperations: () => Promise<ApolloQueryResult<OrderOperationSearchHits>>
  setCreatedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
  setFailedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
}

const OrderLinesModificationAction = ({
  operation,
  order,
  orderHistory,
  getCustomCode,
  setShow,
  refetchOperations,
  setCreatedOperations,
  setFailedOperations
}: Props) => {
  const [paymentAction, setPaymentAction] = useState<graphql.InputActionPaymentOrderModificationOrderLines>({
    actionType: graphql.InputActionType.Auto
  })
  // These are needed to satisfy StartOperation component interface
  const [giftCardAction, setGiftCardAction] = useState<graphql.InputActionGiftCard>({
    actionType: graphql.InputActionType.Auto
  })
  const [bonusAction, setBonusAction] = useState<graphql.InputActionBonus>({
    actionType: graphql.InputActionType.Auto
  })

  // Cast to the specific search hit type to access orderLines
  const searchOperation = operation as OrderOrderLinesModificationSearchHit
  const searchOrderLines = searchOperation.orderLines || []

  // Get modification from Order for totals (not available in search) and provider info
  const modification = order?.Modifications?.find(
    (mod): mod is OrderModificationOrderLines =>
      mod.__typename === 'OrderModificationOrderLines' && mod.id === operation.id
  )

  const [startOrderLinesModification, { loading }] = useMutation(START_ORDER_LINES_MODIFICATION, {
    onCompleted: () => {
      setShow(false)
      setCreatedOperations((prev) => prev?.filter((hit) => hit.id !== operation?.id))
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const [restartOrderLinesModification, { loading: restartLoading }] = useMutation(RESTART_ORDER_LINES_MODIFICATION, {
    onCompleted: () => {
      setShow(false)
      setFailedOperations((prev) => prev?.filter((hit) => hit.id !== operation?.id))
      refetchOperations()
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const startOperation = () => {
    startOrderLinesModification({
      variables: {
        modificationId: operation.id,
        input: {
          payment: paymentAction
        }
      }
    })
  }

  const restartOperation = () => {
    restartOrderLinesModification({
      variables: {
        modificationId: operation.id,
        input: {
          payment: paymentAction
        }
      }
    })
  }

  const isModificationFailed = modification?.failed && !modification.completed
  const hasPaymentProvider = !!modification?.paymentProvider

  // Helper to extract line data from search results (handles union types)
  type LineDataInfo = {
    id: string
    name: string
    quantity: number
    totalPrice: number
  }

  const extractLineData = (lineData: graphql.OrderLineData | undefined | null): LineDataInfo | null => {
    if (!lineData) return null

    const typename = lineData.__typename

    // OrderLineUpdate has nested price object
    if (typename === "OrderLineUpdate") {
      const data = lineData as graphql.OrderLineUpdate
      return {
        id: data.id,
        name: data.displayName || data.name || data.id,
        quantity: data.price?.quantity ?? 0,
        totalPrice: data.price?.totalPriceAmount ?? 0
      }
    }

    // OrderLineCreate has flat fields
    if (typename === "OrderLineCreate") {
      const data = lineData as graphql.OrderLineCreate
      return {
        id: data.id,
        name: data.name || data.id,
        quantity: data.quantity ?? 0,
        totalPrice: data.totalPriceAmount ?? 0
      }
    }

    // OrderLineDelete has flat fields
    if (typename === "OrderLineDelete") {
      const data = lineData as graphql.OrderLineDelete
      return {
        id: data.id,
        name: data.name || data.id,
        quantity: data.quantity ?? 0,
        totalPrice: data.totalPriceAmount ?? 0
      }
    }

    return null
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "CREATE": return "Added"
      case "DELETE": return "Deleted"
      case "UPDATE": return "Modified"
      default: return type
    }
  }

  const renderOrderLinesTable = () => {
    if (searchOrderLines.length === 0) return null

    return (
      <OrderLinesTable>
        <TableHeader>
          <tr>
            <th>Type</th>
            <th style={{ textAlign: "left" }}>Name</th>
            <th>Before Qty</th>
            <th>After Qty</th>
            <th>Before Value</th>
            <th>After Value</th>
          </tr>
        </TableHeader>
        <TableBody>
          {searchOrderLines.map((line, index) => {
            const afterData = extractLineData(line.data)
            const beforeData = line.prev ? extractLineData(line.prev) : null
            const modificationType = line.modificationType || "UPDATE"

            if (!afterData) return null

            return (
              <tr key={afterData.id || index}>
                <td>
                  <TypeBadge $type={modificationType}>
                    {getTypeLabel(modificationType)}
                  </TypeBadge>
                </td>
                <td>{afterData.name}</td>
                <td>
                  {modificationType === "CREATE" ? "—" : (beforeData?.quantity ?? "—")}
                </td>
                <td>
                  {modificationType === "DELETE" ? "—" : (
                    <>
                      {afterData.quantity}
                    </>
                  )}
                </td>
                <td>
                  {modificationType === "CREATE" ? "—" : (
                    beforeData ? (
                      <Money
                        amount={beforeData.totalPrice}
                        currencyUnit={order.currencyCode as DineroFactory.Currency}
                      />
                    ) : "—"
                  )}
                </td>
                <td>
                  {modificationType === "DELETE" ? "—" : (
                    <>
                      <Money
                        amount={afterData.totalPrice}
                        currencyUnit={order.currencyCode as DineroFactory.Currency}
                      />
                    </>
                  )}
                </td>
              </tr>
            )
          })}
        </TableBody>
      </OrderLinesTable>
    )
  }

  const renderTotalsComparison = () => {
    const oldTotals = modification?.oldTotals
    const newTotals = modification?.newTotals

    if (!oldTotals || !newTotals) return null

    const subTotalDiff = (newTotals.subTotal ?? 0) - (oldTotals.subTotal ?? 0)
    const discountDiff = (newTotals.discountTotal ?? 0) - (oldTotals.discountTotal ?? 0)
    const taxDiff = (newTotals.taxTotal ?? 0) - (oldTotals.taxTotal ?? 0)
    const grandTotalDiff = newTotals.grandTotal - oldTotals.grandTotal

    return (
      <TotalsComparison>
        <TotalsHeader>
          <span></span>
          <span style={{ textAlign: "right" }}>Before</span>
          <span style={{ textAlign: "right" }}>After</span>
          <span style={{ textAlign: "right" }}>Diff</span>
        </TotalsHeader>

        {oldTotals.subTotal !== undefined && newTotals.subTotal !== undefined && (
          <TotalsRow>
            <TotalsLabel>Subtotal</TotalsLabel>
            <TotalsCell $type="before">
              <Money
                amount={oldTotals.subTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalsCell>
            <TotalsCell $type="after">
              <Money
                amount={newTotals.subTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalsCell>
            <TotalsCell $type="diff">
              {subTotalDiff !== 0 ? (
                <Money
                  amount={subTotalDiff}
                  currencyUnit={order.currencyCode as DineroFactory.Currency}
                />
              ) : "—"}
            </TotalsCell>
          </TotalsRow>
        )}

        {oldTotals.discountTotal !== undefined && newTotals.discountTotal !== undefined && (
          <TotalsRow>
            <TotalsLabel>Discount</TotalsLabel>
            <TotalsCell $type="before">
              -<Money
                amount={oldTotals.discountTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalsCell>
            <TotalsCell $type="after">
              -<Money
                amount={newTotals.discountTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalsCell>
            <TotalsCell $type="diff">
              {discountDiff !== 0 ? (
                <Money
                  amount={discountDiff}
                  currencyUnit={order.currencyCode as DineroFactory.Currency}
                />
              ) : "—"}
            </TotalsCell>
          </TotalsRow>
        )}

        {oldTotals.taxTotal !== undefined && newTotals.taxTotal !== undefined && (
          <TotalsRow>
            <TotalsLabel>Tax</TotalsLabel>
            <TotalsCell $type="before">
              <Money
                amount={oldTotals.taxTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalsCell>
            <TotalsCell $type="after">
              <Money
                amount={newTotals.taxTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalsCell>
            <TotalsCell $type="diff">
              {taxDiff !== 0 ? (
                <Money
                  amount={taxDiff}
                  currencyUnit={order.currencyCode as DineroFactory.Currency}
                />
              ) : "—"}
            </TotalsCell>
          </TotalsRow>
        )}

        <TotalsRow $highlight>
          <TotalsLabel>Grand Total</TotalsLabel>
          <TotalsCell $type="before">
            <Money
              amount={oldTotals.grandTotal}
              currencyUnit={order.currencyCode as DineroFactory.Currency}
            />
          </TotalsCell>
          <TotalsCell $type="after">
            <Money
              amount={newTotals.grandTotal}
              currencyUnit={order.currencyCode as DineroFactory.Currency}
            />
          </TotalsCell>
          <TotalsCell $type="diff">
            {grandTotalDiff !== 0 ? (
                <Money
                  amount={grandTotalDiff}
                  currencyUnit={order.currencyCode as DineroFactory.Currency}
                />
            ) : "—"}
          </TotalsCell>
        </TotalsRow>
      </TotalsComparison>
    )
  }

  const capitalize = (s: string) => s && s[0].toUpperCase() + s.slice(1)
  const getLabel = (value: string) => capitalize(value.toLowerCase())

  // Wait for order data to load
  if (!order) {
    return null
  }

  if (isModificationFailed) {
    return (
      <>
        <OrderLabel>
          Order: {order.reference}{" "}
          <Link to={`/orders/${order.id}`} target="_blank">
            <ArrowIcon />
          </Link>
        </OrderLabel>
        <Container>
          <Title>
            <div>
              <h2>Failed Modification order lines</h2>
            </div>
          </Title>
          <span>id: {modification.id} <CopyButton string={modification.id} /></span>

          {hasPaymentProvider && (
            <ProviderWrapper>
              <h4>Failed providers:</h4>
              <Provider>Payment</Provider>
            </ProviderWrapper>
          )}

          <CartItems>
            <Label style={{ marginTop: "16px" }}>Order Lines</Label>
            {renderOrderLinesTable()}

            <Reason reason={modification?.reason} getCustomCode={getCustomCode} />

            {renderTotalsComparison()}
          </CartItems>

          <ActionInfoBox showManual={false} />

          <Providers>
            {hasPaymentProvider && (
              <div>
                <ActionWrapper>
                  <LabelWrapper>
                    <PaymentIcon />
                    <b>Payment - {modification.paymentProvider?.providerName}</b>
                  </LabelWrapper>
                  <ActionDropdownButton title={getLabel(paymentAction.actionType)}>
                    <li onClick={() => setPaymentAction({ actionType: graphql.InputActionType.Auto })}>
                      Auto
                    </li>
                    <li onClick={() => setPaymentAction({ actionType: graphql.InputActionType.Manual })}>
                      Manual
                    </li>
                    <li onClick={() => setPaymentAction({ actionType: graphql.InputActionType.Skip })}>
                      Skip
                    </li>
                  </ActionDropdownButton>
                </ActionWrapper>
              </div>
            )}
          </Providers>

          <ErrorMessages
            orderHistory={orderHistory.history}
            operationId={modification.id}
            operation={"ORDER_LINES_MODIFICATION"}
          />

          <ButtonWrapper>
            <SecondaryButton handleClick={restartOperation} loading={restartLoading}>
              Restart modification
            </SecondaryButton>
          </ButtonWrapper>
        </Container>
      </>
    )
  }

  return (
    <>
      <CartContainer>
        <OrderLabel>
          Order: {order.reference}{" "}
          <Link to={`/orders/manage-order/${order.id}`} target="_blank">
            <ArrowIcon />
          </Link>
        </OrderLabel>
        <CartItems>
          <Label style={{ marginTop: "16px" }}>Order Lines</Label>
          {renderOrderLinesTable()}

          <Reason reason={modification?.reason} getCustomCode={getCustomCode} />

          {renderTotalsComparison()}
        </CartItems>
        <StartOperation
          operation={modification}
          actionState={{
            giftCardAction,
            setGiftCardAction,
            bonusAction,
            setBonusAction,
            paymentAction,
            setPaymentAction
          }}
          startOperation={startOperation}
          loading={loading}
        />
      </CartContainer>
    </>
  )
}

export default OrderLinesModificationAction
